import { useCallback, useState, type FunctionComponent } from "react";
import { ClockCircleOutlined, CreditCardOutlined } from "@ant-design/icons";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import {
  Alert,
  Badge,
  Button,
  DatePicker,
  Descriptions,
  Form,
  Grid,
  message,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Tag,
  Typography,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";

import { productClientActions } from "@fitness-app/app-store";
import { ClientArchiveReason, type SubscriptionProductClient } from "@fitness-app/data-models/entities/ProductClient";
import { areInstallmentsSettled } from "@fitness-app/utils/src/products/areInstallmentsSettled";
import { getProductClientAccessEnd } from "@fitness-app/utils/src/products/getProductClientAccessEnd";

import { useEntityChange } from "~/hooks/useEntityChange";
import {
  isPrepaidInstallmentsTrial,
  SUBSCRIPTION_STATUS_MAPPER,
} from "~/modules/Products/constants/subscriptionStatusMapper";
import InfoAboutExtendedAccessPeriod from "~/modules/Products/Product/ProductClientsList/components/InfoAboutExtendedAccessPeriod/InfoAboutExtendedAccessPeriod";
import { PaymentTypeTag } from "~/modules/Products/Product/ProductClientsList/components/PaymentTypeTag";
import { ScheduledAccessTag } from "~/modules/Trainee/TraineeProfile/components/TraineeAccess";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";
import AccessPeriodModal from "./AccessPeriodModal";
import { describeExtendAccessPeriodError } from "./extendAccessPeriodErrorMessage";
import ChangeInstallmentPlanModal from "./ChangeInstallmentPlanModal";
import { PayoffOfferActions } from "./PayoffOfferActions";

interface OwnProps {
  productClient: SubscriptionProductClient;
  updateInvoiceRequest: (value: boolean) => void;
  ownerView?: boolean;
}

type Props = OwnProps;

const DATE_FORMAT = "DD.MM.YYYY (HH:mm)";

interface FormModel {
  original_access_date: null | Dayjs;
  resumes_at: null | Dayjs;
  extensionType: "freePeriodAfterEnd" | "pauseCollection";
}

interface FormModelForPaymentDate {
  currentPeriodEnd: Dayjs;
  newBillingAnchor: null | Dayjs;
  keepPeriodEndAfterUpdateBillingAnchor: boolean;
}

const { useBreakpoint } = Grid;

const ProductClientSubscriptionSettings: FunctionComponent<Props> = ({
  productClient,
  updateInvoiceRequest,
  ownerView,
}) => {
  const { t } = useTranslation(["client", "products", "common"]);
  const elements = useElements();
  const stripe = useStripe();
  const { lg } = useBreakpoint();
  const [showPaymentMethodModal, togglePaymentMethodModal] = useState(false);
  const [loader, onSuccess, onFailure, onStart] = useEntityChange(() => {
    togglePaymentMethodModal(false);
  });
  const dispatch = useAppDispatch();
  const [showModalToUpdateAccessPeriod, toggleModalForAccessPeriod] = useState(false);
  const [showModalToUpdatePaymentDate, toggleModalToUpdatePaymentDate] = useState(false);
  const [showModalToChangeInstallmentPlan, toggleModalToChangeInstallmentPlan] = useState(false);
  const [formController] = Form.useForm<FormModel>();
  const [formControllerForPaymentDate] = Form.useForm<FormModelForPaymentDate>();
  const [showModalLoader, toggleModalLoader] = useState(false);
  const accessEnd = productClient.accessPeriod ? dayjs.unix(productClient.accessPeriod?.currentPeriodEnd) : null;
  const isInstallments = Boolean(productClient.accessPeriod?.installments);

  const installmentsEnd = productClient.accessPeriod?.installments
    ? dayjs.unix(Number(productClient.accessPeriod?.installmentEndsAt) || Number(productClient.accessPeriod?.endDate))
    : null;

  const finalEndDate = getProductClientAccessEnd(productClient);

  // Open the calendar on the first day that is actually selectable — the day after
  // access ends, scheduled packages included. Anchored on `accessPeriod` alone it
  // opened months before the earliest allowed date (TRE-1778).
  const defaultValue = finalEndDate
    ? dayjs.unix(finalEndDate).add(1, "day")
    : productClient.accessPeriod
      ? dayjs
          .unix(
            isInstallments
              ? Number(productClient.accessPeriod?.installmentEndsAt || productClient.accessPeriod?.endDate)
              : productClient.accessPeriod?.pauseCollection?.resumes_at || productClient.accessPeriod?.currentPeriodEnd,
          )
          .add(isInstallments ? 0 : 1, "day")
      : null;

  const getDisabledDate = useCallback(
    (current: dayjs.Dayjs) => {
      if (finalEndDate) {
        return current && finalEndDate ? current < dayjs.unix(finalEndDate)?.endOf("day") : true;
      }

      if (isInstallments) {
        return current && installmentsEnd ? current < installmentsEnd?.endOf("day") : true;
      }

      return current && accessEnd ? current < accessEnd?.endOf("day") : true;
    },
    [accessEnd, finalEndDate, isInstallments],
  );

  const getDisabledDateForPaymentDate = useCallback(
    (current: dayjs.Dayjs) => {
      if (productClient.accessPeriod.currentPeriodEnd) {
        return !(
          current > dayjs.unix(productClient.accessPeriod.currentPeriodEnd) &&
          current <
            dayjs
              .unix(productClient.accessPeriod.currentPeriodEnd)
              .add(1, productClient.accessPeriod.recurring?.interval ?? "month")
        );
      }

      return false;
    },
    [productClient.accessPeriod],
  );

  const handleFormFinish = async (formData: FormModel) => {
    toggleModalLoader(true);
    try {
      await dispatch(
        productClientActions.setExtendAccessPeriod({
          productId: productClient.productId,
          clientEmail: productClient.email,
          // End of the selected day: access should last through the whole day,
          // not be cut at 00:00 (premature archive ~24h early).
          resume_at: formData.resumes_at ? formData.resumes_at.endOf("day").unix() : null,
          extensionType: formData.extensionType,
        }),
      ).unwrap();
      toggleModalForAccessPeriod(false);
      toggleModalLoader(false);
      void message.success(t("pauseCollection.waitToSuccess"));
    } catch (e) {
      void message.error(describeExtendAccessPeriodError(e, t));
      toggleModalLoader(false);
    }
  };

  const handleFormFinishForPaymentDate = async (formData: FormModelForPaymentDate) => {
    toggleModalLoader(true);
    try {
      await dispatch(
        productClientActions.setExtendAccessPeriod({
          productId: productClient.productId,
          clientEmail: productClient.email,
          newBillingAnchor: formData.newBillingAnchor
            ? formData.newBillingAnchor
                .set("hour", formData.currentPeriodEnd.get("hour"))
                .set("minute", formData.currentPeriodEnd.get("minute"))
                .set("second", formData.currentPeriodEnd.get("second"))
                .unix()
            : null,
          keepPeriodEndAfterUpdateBillingAnchor: formData.keepPeriodEndAfterUpdateBillingAnchor ?? true,
        }),
      ).unwrap();
      toggleModalForAccessPeriod(false);
      toggleModalLoader(false);
      toggleModalToUpdatePaymentDate(false);
      void message.success(t("pauseCollection.waitToSuccess"));
    } catch (e) {
      void message.error(describeExtendAccessPeriodError(e, t));
      toggleModalLoader(false);
    }
  };

  const handleCancelSubscription = async () => {
    void message.loading(t("processingRequest"));
    await dispatch(
      productClientActions.cancelSubscriptionAtCurrentPeriodEnd({
        payload: {
          authorId: productClient.author.id,
          productId: productClient.productId,
          isOwner: ownerView,
          clientEmail: productClient.email,
        },
        onSuccess: () => {
          message.destroy();
          void message.success(t("subscription.canceledAtPeriodEnd"));
        },
        onFailure: () => void message.error(t("errors.contactSupport")),
      }),
    );
  };

  const updatePaymentMethod = async () => {
    const cardElement = elements?.getElement(CardElement);
    onStart();
    const res = await stripe?.createPaymentMethod({
      type: "card",
      card: cardElement!,
    });

    if (!res?.paymentMethod) {
      onFailure();
      return;
    }
    void dispatch(
      productClientActions.updatePaymentMethodForSubscription({
        payload: {
          paymentMethodId: res.paymentMethod?.id,
          productId: productClient.productId,
          authorId: productClient.author.id,
          paymentGatewayCustomerId: productClient.paymentGatewayCustomerId,
        },
        onFailure,
        onSuccess,
      }),
    );
  };

  const handleSubscriptionResume = async () => {
    void message.loading(t("processingRequest"));
    await dispatch(
      productClientActions.resumeSubscriptionAtCurrentPeriodEnd({
        payload: {
          authorId: productClient.author.id,
          clientEmail: productClient.email,
          productId: productClient.productId,
        },
        onSuccess: () => {
          message.destroy();
          void message.success(t("subscription.resumedAtPeriodEnd"));
        },
        onFailure: () => void message.error(t("errors.contactSupport")),
      }),
    );
  };

  const handleMigrateToManualPayments = async () => {
    void message.loading(t("processingRequest"));

    if (!productClient.accessPeriod.subscriptionId) {
      void message.error("Brak informacji o subscription id");
      return;
    }

    await dispatch(
      productClientActions.migrateSubscriptionToManualPayments({
        payload: {
          subscriptionId: productClient.accessPeriod.subscriptionId,
          productId: productClient.productId,
          clientId: productClient.id,
        },
        onSuccess: () => {
          message.destroy();
          void message.success(t("subscription.migratedToManualPayments"));
        },
        onFailure: () => void message.error(t("errors.contactSupport")),
      }),
    );
  };

  const openPaymentMethodModal = () => {
    togglePaymentMethodModal(true);
    setTimeout(() => {
      const cardElement = elements?.getElement(CardElement);
      cardElement?.focus();
    }, 300);
  };

  const closePaymentMethodModal = () => {
    const cardElement = elements?.getElement(CardElement);
    cardElement?.clear();
    togglePaymentMethodModal(false);
  };

  const subscriptionEnd = isInstallments
    ? dayjs
        .unix(productClient.archivedInfo?.archivedAt || productClient.accessPeriod.currentPeriodEnd)
        .format(DATE_FORMAT)
    : dayjs
        .unix(
          productClient.archivedInfo?.archivedAt ||
            productClient.accessPeriod.pauseCollection?.resumes_at ||
            productClient.accessPeriod.currentPeriodEnd,
        )
        .format(DATE_FORMAT);

  // A payoff (TRE-1511) settles every remaining installment in a single charge and
  // cancels the Stripe schedule WITHOUT advancing cycleNumber, so "all cycles billed"
  // is no longer the only way to be done. accessPeriod.status === "paid" is the
  // canonical "nothing left to charge" — without it the UI offers a next payment date
  // and a destructive "cancel installments" action on a fully settled plan.
  const installmentsPaid = areInstallmentsSettled(productClient.accessPeriod);

  /** Paid off early — settled without every cycle having been billed. */
  const isPaidOff =
    productClient.accessPeriod.status === "paid" &&
    typeof productClient.accessPeriod.installments === "number" &&
    productClient.accessPeriod.cycleNumber < productClient.accessPeriod.installments;

  const installmentCyclePeriod = productClient.accessPeriod.cycleNumber;

  // productClient.clientOrders is what fetchProductClientOrders fills on this page;
  // productOrders.orders belongs to the product-level transactions view and is empty
  // here (same slice mix-up as the archive modal, TRE-1994).
  const clientOrders = useAppSelector((store) => store.productClient.clientOrders);
  // Recurring BLIK (model A) installments have no Stripe payment method saved —
  // detect them from the client's orders to label the payment method row.
  // The email guard stays as protection against a stale slice from a previous client.
  const hasBlikRecurring = clientOrders.some(
    (order) =>
      order.paymentMethodType === "tpay_blik_recurring" &&
      order.clientEmail?.toLowerCase() === productClient.email?.toLowerCase(),
  );

  const isWarmupPeriod = productClient.accessPeriod.warmupPeriod && installmentCyclePeriod === 0;
  const warumpEndDate = productClient.accessPeriod.warmupPeriod?.endDate
    ? dayjs.unix(productClient.accessPeriod.warmupPeriod.endDate).format(DATE_FORMAT)
    : null;

  return (
    <>
      <Descriptions bordered labelStyle={{ width: 250 }} layout={lg ? "horizontal" : "vertical"}>
        <Descriptions.Item label={t("list.columns.paymentType")} span={3}>
          <PaymentTypeTag productClient={productClient} />
        </Descriptions.Item>
        <Descriptions.Item label={t("list.columns.subscriptionType")} span={3}>
          <Space>
            {t(`products:recurring.${productClient.accessPeriod.recurring?.interval}`, {
              count: productClient.accessPeriod.recurring?.interval_count,
            })}
          </Space>
        </Descriptions.Item>
        {productClient.accessPeriod.installments ? (
          <Descriptions.Item label={t("client:installments.cycle")} span={3}>
            {installmentsPaid || installmentCyclePeriod > (productClient.accessPeriod.installments ?? 0)
              ? productClient.accessPeriod.installments
              : installmentCyclePeriod}{" "}
            / {productClient.accessPeriod.installments}
            {isPaidOff ? (
              <Tag color="green" className="ml-4">
                Dopłacone jednorazowo
              </Tag>
            ) : null}
            {isWarmupPeriod && productClient.accessPeriod.warmupPeriod ? (
              <Tag color="blue" className="ml-4">
                Dodatkowy okres startowy{" "}
                {dayjs.unix(productClient.accessPeriod.warmupPeriod.startDate).format("DD.MM.YYYY")}-{" "}
                {dayjs.unix(productClient.accessPeriod.warmupPeriod.endDate).format("DD.MM.YYYY")}
              </Tag>
            ) : null}
            {ownerView ? <PayoffOfferActions productClient={productClient} /> : null}
            {ownerView &&
            !productClient.archivedInfo &&
            !installmentsPaid &&
            productClient.accessPeriod.status !== "canceled" ? (
              <Button type="link" onClick={() => toggleModalToChangeInstallmentPlan(true)}>
                Zmień plan rat
              </Button>
            ) : null}
          </Descriptions.Item>
        ) : null}
        <Descriptions.Item label={t("list.columns.subscriptionStatus")} span={3}>
          <Space>
            <Badge
              status={
                productClient.accessPeriod.cancelAtPeriodEnd
                  ? SUBSCRIPTION_STATUS_MAPPER.canceled
                  : isPrepaidInstallmentsTrial(productClient.accessPeriod)
                    ? "success"
                    : SUBSCRIPTION_STATUS_MAPPER[productClient.accessPeriod.status]
              }
              text={`${t(
                `common:subscriptionStatus.${
                  isPrepaidInstallmentsTrial(productClient.accessPeriod)
                    ? "trialingInstallments"
                    : productClient.accessPeriod.status
                }`,
              )} ${productClient.archivedInfo?.reason === ClientArchiveReason.Refund ? t("subscription.refund") : ""}`}
            />
            {productClient.accessPeriod.cancelAtPeriodEnd ? (
              <Tag icon={<ClockCircleOutlined />}>
                {t("subscription.endAt")} {dayjs.unix(productClient.accessPeriod.currentPeriodEnd).format("DD.MM")}
              </Tag>
            ) : null}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label={t("list.columns.currentPaymentPeriod")} span={3}>
          <Space size={4} direction="horizontal">
            <span style={{ marginRight: 8 }}>
              {dayjs.unix(productClient.accessPeriod.currentPeriodStart).format(DATE_FORMAT)} -{" "}
              {isWarmupPeriod && warumpEndDate ? warumpEndDate : subscriptionEnd}
            </span>

            <InfoAboutExtendedAccessPeriod accessPeriod={productClient.accessPeriod} isInstallments={isInstallments} />
            {!productClient.archivedInfo && ownerView && !productClient.accessPeriod.cancelAtPeriodEnd && (
              <Button type="link" onClick={() => toggleModalForAccessPeriod(true)}>
                {t("pauseCollection.extendAccessPeriod")}
              </Button>
            )}
          </Space>
        </Descriptions.Item>
        {!installmentsPaid && (
          <Descriptions.Item label={t("list.columns.nextPaymentDate")} span={3}>
            <Space>
              {productClient.accessPeriod.cancelAtPeriodEnd ? (
                <span>
                  {t("subscription.canceled")}
                  <Button className="ml-0" type="link" onClick={handleSubscriptionResume}>
                    {t("subscription.resume")}
                  </Button>
                </span>
              ) : (
                <span>
                  {productClient.archivedInfo ? "-" : subscriptionEnd}
                  {productClient.downgradedToFromNextPeriod ? (
                    <Typography.Text strong style={{ margin: "0 5px" }}>
                      - {(productClient.downgradedToFromNextPeriod.amount / 100).toFixed(2)}{" "}
                      {productClient.downgradedToFromNextPeriod.currency.toUpperCase()}
                    </Typography.Text>
                  ) : (
                    ""
                  )}
                  {productClient.accessPeriod.status !== "imported" &&
                  productClient.accessPeriod.status !== "canceled" ? (
                    <>
                      <Popconfirm title={t("subscription.sureToCancel")} onConfirm={handleCancelSubscription}>
                        <Button className="ml-0" type="link" danger>
                          {t(isInstallments ? "subscription.cancelInstallments" : "subscription.cancel")}
                        </Button>
                      </Popconfirm>
                    </>
                  ) : null}
                </span>
              )}

              {!productClient.archivedInfo && ownerView && productClient.accessPeriod.currentPeriodEnd && (
                <Button
                  type="link"
                  onClick={() => toggleModalToUpdatePaymentDate(true)}
                  disabled={productClient.scheduledAccessPeriod?.some((item) => item.type === "recurring")}
                >
                  Zmień dzień płatności
                </Button>
              )}
            </Space>
          </Descriptions.Item>
        )}
        {productClient.accessPeriod.installmentEndsAt && productClient.accessPeriod.status !== "canceled" ? (
          <Descriptions.Item label={t("list.columns.installmentEndsAt")} span={3}>
            <Space>
              <span>
                {dayjs
                  .unix(
                    productClient.accessPeriod.pauseCollection?.resumes_at ||
                      productClient.accessPeriod.endDate ||
                      productClient.accessPeriod.installmentEndsAt,
                  )
                  .format(DATE_FORMAT)}
              </span>
              <ScheduledAccessTag
                scheduledAccessPeriod={productClient.scheduledAccessPeriod}
                accessPeriod={productClient.accessPeriod}
              />
            </Space>
          </Descriptions.Item>
        ) : null}

        {productClient.accessPeriod.customDuration && (
          <Descriptions.Item label="Cykl dostępu" span={3}>
            <Typography.Text>
              {t(`products:recurring.${productClient.accessPeriod.customDuration.unit}`, {
                count: productClient.accessPeriod.customDuration.count,
              })}
            </Typography.Text>
          </Descriptions.Item>
        )}

        {ownerView ? (
        <Descriptions.Item label={t("list.columns.paymentMethod")} span={3} className="flex flex-row gap-2">
          {productClient.paymentMethod ? (
            <Space>
              <div className="flex flex-col md:flex-row md:items-center">
                <div>
                  <CreditCardOutlined style={{ marginRight: 5 }} /> {productClient.paymentMethod.brand.toUpperCase()}{" "}
                  <span className="hidden md:inline">**** **** **** </span>
                  {productClient.paymentMethod.last4}
                </div>
                {!ownerView && (
                  <Button type="link" onClick={openPaymentMethodModal} loading={!!loader}>
                    {t("subscription.updatePaymentMethod")}
                  </Button>
                )}
              </div>
            </Space>
          ) : hasBlikRecurring ? (
            <Space>
              <CreditCardOutlined style={{ marginRight: 5 }} />
              <span className="capitalize">
                {t("products:productTransactions.paymentMethodType.tpay_blik_recurring")}
              </span>
            </Space>
          ) : (
            <span>-</span>
          )}

          {/* Gated on the subscription, NOT on `paymentMethod`: the card details are a
              display concern, and MigrateSubscriptionToManualPayments only needs a recurring
              subscription id. A card payer whose `paymentMethod` was never linked (TRE-1834)
              is still on autopay and still has to be able to switch off it. */}
          {productClient.accessPeriod.subscriptionId &&
          !productClient.accessPeriod.isManualPayment &&
          !productClient.archivedInfo &&
          !installmentsPaid &&
          productClient.accessPeriod.status !== "canceled" ? (
            <Popconfirm
              title={t("subscription.sureToMigrateToManualPayments")}
              description={t("subscription.migrateToManualPaymentsDescription")}
              onConfirm={handleMigrateToManualPayments}
            >
              <Button className="ml-0" type="link">
                {t("subscription.migrateToManualPayments")}
              </Button>
            </Popconfirm>
          ) : null}
        </Descriptions.Item>
        ) : null}
        {ownerView && productClient.accessPeriod.subscriptionId ? (
          <Descriptions.Item label="Stripe Subscription ID" span={3}>
            <Typography.Link
              target="_blank"
              href={`https://dashboard.stripe.com/subscriptions/${productClient.accessPeriod.subscriptionId}`}
            >
              {productClient.accessPeriod.subscriptionId}
            </Typography.Link>
          </Descriptions.Item>
        ) : null}
        {typeof productClient.invoiceRequested === "boolean" ? (
          <Descriptions.Item label={t("list.columns.requestedInvoice")} span={3}>
            <Switch checked={productClient.invoiceRequested} onChange={updateInvoiceRequest} />
          </Descriptions.Item>
        ) : null}
      </Descriptions>
      <Modal
        open={showPaymentMethodModal}
        title={t("subscription.updatePaymentMethodTitle")}
        onOk={updatePaymentMethod}
        onCancel={closePaymentMethodModal}
        okButtonProps={{
          loading: !!loader,
        }}
      >
        <div style={{ padding: "16px 8px", border: "1px solid #f0f0f0" }}>
          <CardElement
            options={{
              hidePostalCode: true,
              style: {
                base: {
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "rgba(0, 0, 0, 0.85)",
                  "::placeholder": {
                    color: "#aab7c4",
                  },
                },
                invalid: {
                  color: "#f64e60",
                },
              },
            }}
          />
        </div>
      </Modal>
      {ownerView && (
        <AccessPeriodModal
          open={showModalToUpdateAccessPeriod}
          onCancel={() => toggleModalForAccessPeriod(false)}
          onOk={() => formController?.submit()}
          loading={showModalLoader}
          form={formController}
          onFinish={handleFormFinish}
          isInstallments={isInstallments}
          accessEnd={finalEndDate ? dayjs.unix(finalEndDate) : null}
          getDisabledDate={getDisabledDate}
          defaultValue={defaultValue}
          blockPauseCollection={
            isInstallments ||
            Boolean(productClient.scheduledAccessPeriod && productClient.scheduledAccessPeriod?.length > 0)
          }
        />
      )}
      {ownerView && (
        <ChangeInstallmentPlanModal
          open={showModalToChangeInstallmentPlan}
          onClose={() => toggleModalToChangeInstallmentPlan(false)}
          productClient={productClient}
        />
      )}
      {ownerView && (
        <Modal
          open={showModalToUpdatePaymentDate}
          onCancel={() => toggleModalToUpdatePaymentDate(false)}
          title="Zmień dzień płatności (cykl rozliczeniowy)"
          width={700}
          okText={t("common:button.save")}
          onOk={() => formControllerForPaymentDate?.submit()}
          okButtonProps={{
            loading: showModalLoader,
          }}
        >
          <Alert
            showIcon
            message="Nowa data płatności zostanie zastosowana do wszystkich przyszłych cykli rozliczeniowych. Możesz wybrać datę pomiędzy zakończeniem bieżącego cyklu rozliczeniowego a zakończeniem następnego. Klient nie zapłaci za te dodatkowe dni. Przełącznik poniżej decyduje, co dzieje się z końcem planu: włączony — plan kończy się w pierwotnym terminie, a ostatni cykl jest o te dni krótszy; wyłączony — cały plan przesuwa się razem z datą płatności."
            style={{ marginBottom: 16 }}
          />
          <Form<FormModelForPaymentDate>
            form={formControllerForPaymentDate}
            labelCol={{ xxl: { span: 8, offset: 2 }, span: 24, offset: 0 }}
            wrapperCol={{ xxl: { span: 16, offset: 2 }, span: 24, offset: 0 }}
            onFinish={handleFormFinishForPaymentDate}
            initialValues={{
              currentPeriodEnd: dayjs.unix(productClient.accessPeriod?.currentPeriodEnd),
              newBillingAnchor: dayjs.unix(productClient.accessPeriod?.currentPeriodEnd).add(1, "day"),
              keepPeriodEndAfterUpdateBillingAnchor: true,
            }}
          >
            <Form.Item name="currentPeriodEnd" label={t("pauseCollection.accessTo")}>
              <DatePicker disabled style={{ minWidth: 250 }} />
            </Form.Item>
            <Form.Item name="newBillingAnchor" label="Nowy dzień płatności" rules={[{ required: true }]}>
              <DatePicker
                style={{ minWidth: 250 }}
                disabledDate={getDisabledDateForPaymentDate}
                showNow={false}
                defaultValue={dayjs.unix(productClient.accessPeriod?.currentPeriodEnd).add(1, "day")}
              />
            </Form.Item>
            <Form.Item
              name="keepPeriodEndAfterUpdateBillingAnchor"
              label="Zachowaj datę zakończenia cyklu rozliczeniowego"
            >
              <Switch />
            </Form.Item>
          </Form>
        </Modal>
      )}
    </>
  );
};

export default ProductClientSubscriptionSettings;
